import { useState, useEffect, createContext } from "react";
import {
  getMainView,
  getViewerView,
  getSideViewerView,
  callApi,
} from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from "react-toastify";
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import MainMenuBtn from "./components/MainMenuBtn";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";
import AOS from "aos";
import "aos/dist/aos.css";

export const AppContext = createContext(null);

export default function App(props) {
  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices
  const [isScrolled, setIsScrolled] = useState(false); //tracks if page is scrolled

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);

  //App level data
  const [systemParams, setSystemParams] = useState(null);

  const [allNews, setAllNews] = useState(null);
  const [allReports, setAllReports] = useState(null);
  const [allEvents, setAllEvents] = useState(null);
  const [allGallery, setAllGallery] = useState(null);
  const [allResearch, setAllResearch] = useState(null);
  const [allEthics, setAllEthics] = useState(null);
  const [allPublications, setAllPublications] = useState(null);
  const [allTeamMembers, setAllTeamMembers] = useState(null);

  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);
    /*
    await Promise.all([
      getSystemParams(),
    ]) */
    setReady(true);
  }

  async function getSystemParams() {
    return new Promise(async (resolve) => {
      await callApi("get-system-params", {}).then((response) => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data);
        } else {
          resolve(null);
        }
      });
    });
  }

  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function tellError(msg) {
    toast.error(msg);
  }

  function tellInfo(msg) {
    toast.info(msg);
  }

  function tellWarning(msg) {
    toast.warn(msg);
  }

  function tellMessage(msg) {
    toast.success(msg);
  }

  function openSideBar() {
    appContext.setIsSideBarOpen(true);
  }

  function closeSideBar() {
    appContext.setIsSideBarOpen(false);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      //..

      let url = "";
      if (nav.item) {
        url = `#/${nav.item}/`;
      }

      if (nav.subItem) {
        url += `${nav.subItem}/`;
      }

      if (nav.extraItem) {
        url += `${nav.extraItem}/`;
      }

      if (nav.moreItem) {
        url += `${nav.moreItem}/`;
      }

      window.location.href = url;
      //..
    }
  }

  async function getCurrentUserData() {
    await callApi("get-current-user-data", {}).then((response) => {
      if (response.status === 1) {
        setCurrentUserData(response.data);
      }
    });
  }

  function authCheck() {
    return new Promise(async (resolve) => {
      await callApi("auth-check", {}).then((response) => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true);
        } else {
          setAuth(false);
          resolve(false);
        }
      });
    });
  }

  function activateDialog(params) {
    let { message, onConfirm } = params;
    setConfirmDialogAction(() => {
      return onConfirm;
    });
    setConfirmDialogMessage(message);
    setShowConfirmDialog(true);
  }

  function getAllNews() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-news", {}).then((response) => {
        if (response.status === 1) {
          setAllNews(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllPublications() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-publications", {}).then((response) => {
        if (response.status === 1) {
          setAllPublications(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllReports() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-reports", {}).then((response) => {
        if (response.status === 1) {
          setAllReports(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllResearch() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-research", {}).then((response) => {
        if (response.status === 1) {
          setAllResearch(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllEthics() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-ethics", {}).then((response) => {
        if (response.status === 1) {
          setAllEthics(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllEvents() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-events", {}).then((response) => {
        if (response.status === 1) {
          setAllEvents(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllGallery() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-gallery", {}).then((response) => {
        if (response.status === 1) {
          setAllGallery(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllTeamMembers() {
    return new Promise(async (resolve) => {
      await callApi("get-all-team-members", {}).then((response) => {
        if (response.status === 1) {
          setAllTeamMembers(response.data);
          resolve(response.data);
        } else {
          setAllTeamMembers([]);
          resolve([]);
        }
      });
    });
  }

  useEffect(() => {
    init();
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: true,
      offset: 60,
    });
  }, []);

  // Handle scroll to track if page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle window resize to close sidebar on large screens
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 991 && isSideBarOpen) {
        setIsSideBarOpen(false);
      }
    }

    window.addEventListener("resize", handleResize);

    // Also check on initial load
    if (window.innerWidth > 991 && isSideBarOpen) {
      setIsSideBarOpen(false);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isSideBarOpen]);

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }, [isSideBarOpen]);

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem]);

  useEffect(() => {
    if (currentUserData) {
      //handle current user data changes here
    }
  }, [currentUserData]);

  useEffect(() => {
    //check for viewers
    if (navItem === "view") {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false);
      setSideViewerView(null);
    } else if (navItem === "side-view") {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      setShowViewer(false);
      setViewerView(null);
    } else {
      //just set normal views

      setShowViewer(false);
      setShowSideViewer(false);

      setViewerView(null);
      setSideViewerView(null);

      const _mainView = getMainView(appContext);
      if (_mainView) {
        setMainView(_mainView);
      }

      if (!(!appContext.navItem || appContext.navItem === "home")) {
        setTimeout(() => {
          scrollToTop(); //trigger top scrolling
        }, 200);
      }
    }

    setTimeout(() => {
      AOS.refresh();
    }, 250);
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    isScrolled,
    showOverlayLoader,
    auth,
    currentUserData,
    systemParams,
    getSystemParams,
    openSideBar,
    closeSideBar,
    scrollToTop,
    allNews,
    getAllNews,
    allEvents,
    getAllEvents,
    allGallery,
    getAllGallery,
    allResearch,
    getAllResearch,
    allEthics,
    getAllEthics,
    allReports,
    getAllReports,
    allPublications,
    getAllPublications,
    allTeamMembers,
    getAllTeamMembers,
  };

  if (ready) {
    return (
      <AppContext.Provider value={appContext}>
        <MainBody />
        <TopBar />
        <MainMenuBtn />
        <SideBar />
        <Viewer />
        <SideViewer />
        <ConfirmDialog />
        <OverlayLoader />
        <ToastContainer position="bottom-right" />
      </AppContext.Provider>
    );
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <ToastContainer position="bottom-right" />
        <MainLoader />
      </AppContext.Provider>
    );
  }
}
